"use client";

import { useSearchParams } from "next/navigation";

import type { Cta } from "@/composites/landing/types";
import { appendForwardedParams, pickAllowlisted } from "./index";

/**
 * Adds the visitor's marketing params (today `recruiterId`) to a CTA link.
 *
 * Step by step:
 *   1. A visitor arrives from an email link: /vip-access-pass?recruiterId=123
 *   2. The page HTML is cached and shared with everyone, so it cannot contain
 *      that number. Its CTA links stay exactly as the CMS authored them.
 *   3. This hook runs in the visitor's browser and reads the number from the
 *      address bar.
 *   4. It returns a function that copies a CTA and adds the number to its link.
 *
 * Only params in `FORWARDED_URL_PARAMS` are copied, and only links that stay on
 * our own site are touched — see `appendForwardedParams`.
 */
export function useForwardedCta(): (cta?: Cta) => Cta | undefined {
	const searchParams = useSearchParams();
	const params = pickAllowlisted(
		new URLSearchParams(searchParams.toString())
	);

	return (cta) =>
		cta && { ...cta, href: appendForwardedParams(cta.href, params) };
}
